import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../shared/apiClient";
import { ImageUploadField } from "../../shared/ImageUploadField";
import { SearchableSelectField } from "../../shared/SearchableSelectField";
import { IconList, IconRefresh, IconSave } from "../../shared/toolbarIcons";

const emptyForm = () => ({
  name: "",
  email: "",
  phoneNo: "",
  photo: "",
  gender: "",
  designationId: "",
  departmentId: "",
  address: "",
  countryId: "",
  stateId: "",
  cityId: "",
  pobox: "",
  contactPersonName: "",
  contactPersonMob: ""
});

export function EmployeesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");

  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadLookups = async () => {
    setLoading(true);
    setError(null);
    const [dRes, depRes, coRes, sRes, ciRes] = await Promise.all([
      apiGet("/api/Designations"),
      apiGet("/api/Departments"),
      apiGet("/api/Countries"),
      apiGet("/api/States"),
      apiGet("/api/Cities")
    ]);
    if (dRes.ok) setDesignations(dRes.data);
    if (depRes.ok) setDepartments(depRes.data);
    if (coRes.ok) setCountries(coRes.data);
    if (sRes.ok) setStates(sRes.data);
    if (ciRes.ok) setCities(ciRes.data);
    if (!dRes.ok) setError(dRes.error ?? "Failed to load designations");
    else if (!depRes.ok) setError(depRes.error ?? "Failed to load departments");
    else if (!coRes.ok) setError(coRes.error ?? "Failed to load countries");
    else if (!sRes.ok) setError(sRes.error ?? "Failed to load states");
    else if (!ciRes.ok) setError(ciRes.error ?? "Failed to load cities");
    setLoading(false);
  };

  useEffect(() => {
    void loadLookups();
  }, []);

  useEffect(() => {
    if (!editParam) {
      setEditingId(null);
      setForm(emptyForm());
      setLoading(false);
      return;
    }
    const id = Number(editParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await apiGet(`/api/Employees/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load employee");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        name: x.name ?? "",
        email: x.email ?? "",
        phoneNo: x.phoneNo ?? "",
        photo: x.photo ?? "",
        gender: x.gender ?? "",
        designationId: x.designationId ?? "",
        departmentId: x.departmentId ?? "",
        address: x.address ?? "",
        countryId: x.countryId ?? "",
        stateId: x.stateId ?? "",
        cityId: x.cityId ?? "",
        pobox: x.pobox ?? "",
        contactPersonName: x.contactPersonName ?? "",
        contactPersonMob: x.contactPersonMob ?? ""
      });
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [editParam]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setSearchParams({}, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.designationId || !form.departmentId || !form.countryId || !form.stateId || !form.cityId) return;
    setSaving(true);
    setError(null);
    const res = await apiPost("/api/Employees", {
      id: editingId ?? 0,
      name: form.name || null,
      email: form.email,
      phoneNo: form.phoneNo || null,
      photo: form.photo || null,
      gender: form.gender || null,
      designationId: Number(form.designationId),
      departmentId: Number(form.departmentId),
      address: form.address || null,
      countryId: Number(form.countryId),
      stateId: Number(form.stateId),
      cityId: Number(form.cityId),
      pobox: form.pobox || null,
      contactPersonName: form.contactPersonName || null,
      contactPersonMob: form.contactPersonMob || null
    });
    if (!res.ok) setError(res.error ?? "Failed to save employee");
    else {
      await loadLookups();
      resetForm();
    }
    setSaving(false);
  };

  const onRefresh = async () => {
    setError(null);
    await loadLookups();
    if (editParam) {
      const id = Number(editParam);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      const res = await apiGet(`/api/Employees/${id}`);
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load employee");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        name: x.name ?? "",
        email: x.email ?? "",
        phoneNo: x.phoneNo ?? "",
        photo: x.photo ?? "",
        gender: x.gender ?? "",
        designationId: x.designationId ?? "",
        departmentId: x.departmentId ?? "",
        address: x.address ?? "",
        countryId: x.countryId ?? "",
        stateId: x.stateId ?? "",
        cityId: x.cityId ?? "",
        pobox: x.pobox ?? "",
        contactPersonName: x.contactPersonName ?? "",
        contactPersonMob: x.contactPersonMob ?? ""
      });
    } else {
      setEditingId(null);
      setForm(emptyForm());
    }
  };

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>{editingId ? "Edit Employee" : "Add Employee"}</h2>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="submit"
            form="employees-form"
            disabled={saving}
            title="Save"
            aria-label="Save employee"
          >
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void onRefresh()}
            disabled={saving || loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/employees-list" className="icon-button icon-button--toolbar" title="Employees list" aria-label="Open list">
            <IconList />
          </Link>
        </div>
      </div>
      <div className="card-body">
        <form id="employees-form" onSubmit={onSubmit} className="form form-fields-bottomline">
          <div className="form-row form-row--4">
            <label className="field">
              <span>Name</span>
              <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </label>
            <label className="field">
              <span>Email</span>
              <input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
            </label>
            <label className="field">
              <span>Phone</span>
              <input value={form.phoneNo} onChange={(e) => setForm((p) => ({ ...p, phoneNo: e.target.value }))} />
            </label>
            <label className="field">
              <span>Gender</span>
              <input value={form.gender} onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value }))} />
            </label>
          </div>
          <div className="form-row form-row--4">
            <SearchableSelectField
              label="Designation"
              options={designations}
              value={form.designationId}
              onChange={(nextValue) => setForm((p) => ({ ...p, designationId: nextValue === "" ? "" : Number(nextValue) }))}
              getOptionLabel={(option) => option.name || `Designation #${option.id}`}
              getSearchText={(option) => `${option.name ?? ""} ${option.id ?? ""}`}
              placeholder="Search designation"
            />
            <SearchableSelectField
              label="Department"
              options={departments}
              value={form.departmentId}
              onChange={(nextValue) => setForm((p) => ({ ...p, departmentId: nextValue === "" ? "" : Number(nextValue) }))}
              getOptionLabel={(option) => option.name || `Department #${option.id}`}
              getSearchText={(option) => `${option.name ?? ""} ${option.id ?? ""}`}
              placeholder="Search department"
            />
            <SearchableSelectField
              label="Country"
              options={countries}
              value={form.countryId}
              onChange={(nextValue) =>
                setForm((p) => ({
                  ...p,
                  countryId: nextValue === "" ? "" : Number(nextValue),
                  stateId: "",
                  cityId: ""
                }))
              }
              getOptionLabel={(option) => option.name || `Country #${option.id}`}
              getSearchText={(option) => `${option.name ?? ""} ${option.code ?? ""} ${option.id ?? ""}`}
              placeholder="Search country"
            />
            <SearchableSelectField
              label="State"
              options={states.filter((s) => !form.countryId || s.countryId === Number(form.countryId))}
              value={form.stateId}
              onChange={(nextValue) =>
                setForm((p) => ({
                  ...p,
                  stateId: nextValue === "" ? "" : Number(nextValue),
                  cityId: ""
                }))
              }
              getOptionLabel={(option) => option.name || `State #${option.id}`}
              getSearchText={(option) => `${option.name ?? ""} ${option.code ?? ""} ${option.id ?? ""}`}
              placeholder="Search state"
            />
          </div>
          <div className="form-row form-row--4">
            <SearchableSelectField
              label="City"
              options={cities.filter((c) => !form.stateId || c.stateId === Number(form.stateId))}
              value={form.cityId}
              onChange={(nextValue) => setForm((p) => ({ ...p, cityId: nextValue === "" ? "" : Number(nextValue) }))}
              getOptionLabel={(option) => option.name || `City #${option.id}`}
              getSearchText={(option) => `${option.name ?? ""} ${option.id ?? ""}`}
              placeholder="Search city"
            />
            <label className="field">
              <span>Address</span>
              <input value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} />
            </label>
            <label className="field">
              <span>POBOX</span>
              <input value={form.pobox} onChange={(e) => setForm((p) => ({ ...p, pobox: e.target.value }))} />
            </label>
            <label className="field">
              <span>Contact Person</span>
              <input value={form.contactPersonName} onChange={(e) => setForm((p) => ({ ...p, contactPersonName: e.target.value }))} />
            </label>
          </div>
          <div className="form-row form-row--4">
            <label className="field">
              <span>Contact Mobile</span>
              <input value={form.contactPersonMob} onChange={(e) => setForm((p) => ({ ...p, contactPersonMob: e.target.value }))} />
            </label>
            <ImageUploadField label="Photo Upload" value={form.photo} onChange={(photo) => setForm((p) => ({ ...p, photo }))} />
          </div>
          {error && <div className="error-banner">{error}</div>}
        </form>
      </div>
    </div>
  );
}
